import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import { useParams } from "react-router";
import useLanguageChange from "../../../../hooks/useLanguageChange";

import { DISABLE, ENTER_GUEST_NAME_MAX_LENGTH, PARAMS } from "../../../../constants/Constants";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../../../constants/PublicVariables";

import { handleJoinMeetingAPI } from "../../../../actions/joinMeetingAction";
import { EVENT_NAME } from "../../../../constants/GTagConstants";
import googleEvent from "../../../../services/googleEvent";
import { checkSupportedBrowsers } from "../../../CommonModals/CommonUtils";
import { rejoinNotllowed } from "../../../ParticipantsList/HostControlledParticipants/HostControlledParticipantUtils";
import { getSessionId } from "../../../../shared/utility";

const EnterGuestName = ({ privacyPolicyDataErrorMsg }) => {
    const params = useParams();
    const sessionId = getSessionId(params?.sessionId);
    const dispatch = useDispatch();

    /* Destructuring values from the 'onLoadData' reducer of the Redux store */
    const { privacyPolicyData, privacyPolicyLoading } = useSelector((state) => state.onLoadData);
    const { joinMeetingLoading, joinMeetingError } = useSelector((state) => state.joinMeeting);
    const { mediate } = useSelector((state) => state.silentObserver);

    const [guestName, setGuestName] = useState(CALL_DATA.participantUserName ?? "");
    const [isConnectButtonDisabled, setIsConnectButtonDisabled] = useState(false);
    const [continueButtonText, setContinueButtonText] = useState("");

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    useEffect(() => {
        if (privacyPolicyData) {
            // Extracting search parameters from the current window's location
            const searchParams = new URLSearchParams(window.location.search);
            let paramGuestName = "";
            if (!isEmpty(searchParams.get(PARAMS.GUEST_NAME))) {
                paramGuestName = decodeURIComponent(searchParams.get(PARAMS.GUEST_NAME))?.substring(0, ENTER_GUEST_NAME_MAX_LENGTH);
            } else if (!isEmpty(CALL_DATA.participantUserName)) {
                paramGuestName = CALL_DATA.participantUserName;
            }

            setGuestName(paramGuestName);
            setIsConnectButtonDisabled(privacyPolicyData?.sessionConfigurations.GUESTNAMEREQUIRED === DISABLE && paramGuestName?.trim().length === 0);
        }
    }, []);

    useEffect(() => {
        const buttonText =
            privacyPolicyData?.sessionConfigurations.GUESTNAMEREQUIRED === DISABLE || guestName?.trim().length > 0
                ? localisedValues?.continue_initcap
                : localisedValues?.continue_without_name;
        setContinueButtonText(buttonText);
    }, [localisedValues]);

    useEffect(() => {
        if (joinMeetingLoading) {
            setIsConnectButtonDisabled(true);
        }
    }, [joinMeetingLoading]);

    useEffect(() => {
        if (joinMeetingError) {
            setIsConnectButtonDisabled(false);
        }
    }, [joinMeetingError]);

    useEffect(() => {
        if (privacyPolicyData) {
            setIsConnectButtonDisabled(privacyPolicyData?.sessionConfigurations.GUESTNAMEREQUIRED === DISABLE && guestName?.trim().length === 0);
            if (!isEmpty(localisedValues)) {
                setContinueButtonText(
                    privacyPolicyData?.sessionConfigurations.GUESTNAMEREQUIRED === DISABLE || guestName?.trim().length > 0
                        ? localisedValues?.continue_initcap
                        : localisedValues?.continue_without_name
                );
            }
        }
        CALL_DATA.participantUserName = guestName?.trim();
    }, [guestName]);

    /* Handle the privacy policy popup agreed and decline buttons */
    const dispatchJoinMeeting = () => {
        if (privacyPolicyData?.domainName && (GLOBAL_MEETING_DATA.sessionId || sessionId) && privacyPolicyLoading === false) {
            const req = {
                sessionId: GLOBAL_MEETING_DATA.sessionId || sessionId,
                memberName: GLOBAL_MEETING_DATA.isAgent ? "" : CALL_DATA.participantUserName,
            };

            if (GLOBAL_MEETING_DATA.isAgent) {
                req.userId = GLOBAL_MEETING_DATA.ucid;
            }

            /* requiredRoom key handle if upload only flow available */
            if (PublicVariables.isOnlyUploadImageMeet) {
                req.requiredRoom = false;
            }
            /* handle observer key if observer availab */
            if (CALL_DATA.isObserver && !GLOBAL_MEETING_DATA.isAgent) {
                if (!mediate) {
                    req.observer = true;
                    googleEvent.logEvent(EVENT_NAME.OBSERVER_JOINED);
                    GLOBAL_MEETING_DATA.isGuest = false;
                    CALL_DATA.isObserver = true;
                } else if (CALL_DATA.isObserver && mediate) {
                    googleEvent.logEvent(EVENT_NAME.MEDIATOR_JOINED);
                    GLOBAL_MEETING_DATA.isGuest = true;
                    CALL_DATA.isObserver = false;
                }
            }
            const isBrowserSupported = checkSupportedBrowsers();
            if (isBrowserSupported && !rejoinNotllowed()) dispatch(handleJoinMeetingAPI(req));
        }
    };

    const guestNameChangeHandler = (e) => {
        const val = e.target.value?.substring(0, ENTER_GUEST_NAME_MAX_LENGTH);
        setGuestName(val);
    };

    /* On pressing enter key in input key, perform submit event */
    const submitEventHandler = (e) => {
        if (e.key === "Enter") {
            if (guestName?.trim().length > 0) {
                dispatchJoinMeeting();
            }
        }
    };

    return (
        <div className='guest-name'>
            <div className='guest-name-form'>
                <div className='floating-form-group with-count'>
                    <div className='floating-form-group-in'>
                        <input
                            className='floating-form-control'
                            maxLength={ENTER_GUEST_NAME_MAX_LENGTH}
                            value={guestName}
                            type='text'
                            placeholder={localisedValues?.enter_your_name}
                            onChange={(e) => guestNameChangeHandler(e)}
                            onKeyDown={(e) => submitEventHandler(e)}
                            data-testid='guestNameInput'
                            style={{ fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                        />
                        <span className='floating-label'>
                            <span className='floating-label-in'>{localisedValues?.enter_your_name}</span>
                        </span>
                        <span className='for-border'></span>
                        <span className='for-count' data-testid='guestNameCharCounter'>
                            {ENTER_GUEST_NAME_MAX_LENGTH - guestName.length}
                        </span>
                    </div>
                </div>
            </div>
            <div className='session-form-button-row'>
                <div className='button'>
                    <button
                        className='btn btn-action'
                        data-testid='enterGuestNameContinueButton'
                        onClick={dispatchJoinMeeting}
                        disabled={isConnectButtonDisabled}
                    >
                        {continueButtonText}
                    </button>
                </div>
            </div>
            {privacyPolicyDataErrorMsg && <div className='validation-error session-id-error'>{privacyPolicyDataErrorMsg}</div>}
        </div>
    );
};

EnterGuestName.propTypes = {
    privacyPolicyDataErrorMsg: PropTypes.string,
};

export default EnterGuestName;
